import { Component, registerComponent, registerSystem, System, THREE } from "aframe";
import { AnimationClip, Bone, Camera,  Group, LoadingManager, Material, Object3D, Scene, Skeleton, Vector3Like } from "three";


/*


"model" system : centralizes and caches model loading
"model" component : loads a model and uses it directly in the scene
"model-instance" component : loads a model and creates a shared instance

*/



interface Gltf {
	scene : Scene;
	scenes : Scene[];
	animations : AnimationClip[];
	camera : Camera[];
	userData : any;
}

interface GltfPromise extends Promise<Gltf> 
{
	progress : DocumentFragment
}

interface ModelSystem extends System {
	data : {
		//key : string,
		endpoint : string;
		authentication : string;
		queryString : string;
	},
	manager : LoadingManager;
	load(url : string, progress? : ProgressCallback) : Promise<Gltf>;
	cache : { [url : string] : GltfPromise };
}



registerSystem("model", {
	schema : {
		//key : { type : "string", default : "mesh"},
		endpoint : { type : "string", default : "/Models/" },
		authentication : { type : "string", default : "authenticate-request" },
		queryString : { type: "string", default : "" }, //Additional query parameters passed in each request
	},
	init(this : ModelSystem)
	{
		this.manager = new LoadingManager();
		this.cache = {};
	},
	load(this : ModelSystem, url : string, progressListener ? : ProgressCallback)
	{
		let promise = this.cache[ url ];
		if(!promise)
		{
			const progressDispatcher = document.createDocumentFragment();
			const progress : ProgressCallback = (event) => progressDispatcher.dispatchEvent( new ProgressEvent(event.type, event) );
			promise = new Promise<Gltf>( (resolve, reject) =>
			{
				const loader = new THREE.GLTFLoader(this.manager);
				if( this.data.authentication )
					this.el.emit(this.data.authentication, { headers : loader.requestHeader });
		
				const baseUrl = new URL( this.data.endpoint, this.el.ownerDocument.location.href );

				const modelUrl = new URL( url, baseUrl );
				
				const qs = this.data.queryString;
				if(qs && /^http(s)?:$/.test(modelUrl.protocol))
					modelUrl.search = (modelUrl.search ? (modelUrl.search + "&") : "?") +
						((qs.startsWith("?") || qs.startsWith("&")) ? qs.slice(1) : qs);

				loader.load( modelUrl.href, resolve, progress, reject );
			}) as GltfPromise;
			promise.progress = progressDispatcher;
			this.cache[url] = promise;
		}

		if(progressListener)
		{
			promise.progress.addEventListener("progress", progressListener as EventListener );
			promise.then( 
				(_) => promise.progress.removeEventListener("progress", progressListener as EventListener ),
				(_) => {} 
			);
		}
		
		return promise;
	}
});

type ProgressCallback = (event : ProgressEvent) => void;

interface ModelComponent extends Component
{
	data : string;
	system : ModelSystem;
}
registerComponent("model", {
	schema : { type : "string" },
	update(this : ModelComponent )
	{
		const object3Dkey = this.attrName || this.name;

		if(object3Dkey in this.el.object3DMap)
			this.el.removeObject3D(object3Dkey);

		if(!this.data) return; //empty url

		const progress : ProgressCallback = (event) => this.el.dispatchEvent(new ProgressEvent(event.type, event));
		this.system.load( this.data, progress).then( 
			(gltf) => {
				const rootObject = gltf.scene || gltf.scenes[0];
				rootObject.animations = gltf.animations;
				this.el.setObject3D(object3Dkey, rootObject);
			},
			(reason) => console.dir(reason)
		);
	}
});


interface ModelInstanceComponent extends Component
{
	data : {
		model : string;
		object : string;
		position : Vector3Like;
		rotation : Vector3Like;
		scale : Vector3Like;
		rotationOrder : string;
		animations : string;
		tracks : string;
		materials : boolean;
	}
}




const enumerateObjects = (obj : Object3D, map : { [name:string] : Object3D }, keyOf : (obj : Object3D) => string ) =>
{
	const key = keyOf(obj);
	if(key)
		map[key] = obj;
	for(let child of obj.children)
		enumerateObjects(child, map, keyOf );
}

const buildInstance = (instance : Object3D & { skeleton? : Skeleton }, objects : { [name:string] : Object3D }, keyOf : (obj : Object3D) => string ) =>
{
	let skeleton = instance.skeleton;
	if(skeleton)
	{
		instance.skeleton = skeleton = skeleton.clone();
		skeleton.bones = skeleton.bones.map( b => {
			const key = keyOf(b);
			const bone = key && objects[key] as Bone;
			if(bone) return bone;
			console.warn("Bone not found :", b.name);
			return b;
		});
	}


	for(let child of instance.children)
		buildInstance(child, objects, keyOf);
};

const createInstance = (source : Object3D) =>
{
	const keyOf = (obj : Object3D) => obj.name;

	//const instance : Object3D = (source.type == "Object3D") ? (new Group()) source.clone(true);
	//Root object needs to be a Group as much as possible
	const ctor : any = (source.type == "Object3D") ? Group : source.constructor;
	const instance : Object3D & { sourceObject? : Object3D } = new ctor();
	instance.copy(source, true);
	instance.sourceObject = source;

	const objects : { [name:string] : Object3D} = {};
	enumerateObjects(instance, objects, keyOf);
	buildInstance(instance, objects, keyOf);
	return instance;
};



type MaterialInstance = Material & { sourceMaterial : Material };
const createMaterialInstances = (obj : Object3D & {material? : Material | Material[] }, materials : { [name:string] : MaterialInstance[] }) =>
{
	if(obj.material)
	{
		const oMats = Array.isArray(obj.material) ? [ ...obj.material ] : [ obj.material ];

		for(let ix = oMats.length; --ix >= 0; )
		{
			const oMat = oMats[ix];
			const mats : MaterialInstance[] =  materials[ oMat.name ] || ( materials[ oMat.name ] = [] );

			let mi : MaterialInstance | null = null;
			for(let mat of mats)
				if(mat.sourceMaterial === obj.material)
				{
					mi = mat;
					break;
				}
			if(!mi)
			{
				mi = oMat.clone() as MaterialInstance;
				mi.sourceMaterial = oMat;
				mats.push(mi);
			}
			oMats[ix] = mi;
		}

		obj.material = Array.isArray(obj.material) ? oMats : oMats[0];
	}

	for(let child of obj.children)
		createMaterialInstances(child, materials);
}


const degrees = Math.PI / 180.0;
registerComponent("model-instance", {
	schema : {
		model : { type : "string" },
		object : { type : "string" },
		position : { type : "vec3" },
		rotation : { type : "vec3" },
		scale : {type : "vec3", default : { x:0, y:0, z:0 }},
		rotationOrder : { type : "string" },
		animations : { type : "string" }, //Regex : Which animations to keep
		tracks : { type : "string" }, //Regex : Which tracks to keep
		materials : { type : "boolean", default : false } //True to create instances of source materials
	},
	multiple : true,
	update(this : ModelInstanceComponent)
	{
		const object3Dkey = this.attrName || this.name;

		if(object3Dkey in this.el.object3DMap)
			this.el.removeObject3D(object3Dkey);

		if( !this.data.model) return; //Empty url

		const modelSystem = this.el.sceneEl!.systems.model as ModelSystem;
		const progress : ProgressCallback = (event) => this.el.dispatchEvent(new ProgressEvent(event.type, event));
		modelSystem.load(this.data.model, progress).then( 
			(gltf) => {
				const rootScene = gltf.scene || gltf.scenes[0];
				
				const rootObject = this.data.object ? rootScene.getObjectByName(this.data.object) : rootScene;
				if(!rootObject)
					return console.warn(object3Dkey, "Object not found", this.data.object);
				
				const rootInstance = createInstance(rootObject);

				if(this.data.materials)
					createMaterialInstances(rootInstance, {} );

				rootInstance.position.copy( this.data.position );
				
				const rot = this.data.rotation;
				rootInstance.rotation.set( rot.x * degrees, rot.y * degrees, rot.z * degrees, this.data.rotationOrder || rootInstance.rotation.order );

				const scl = this.data.scale;
				//Only if nonzero (= default) : 
				if((scl.x * scl.y * scl.z) != 0.0)
					rootInstance.scale.copy(scl);

				if(this.data.animations)
				{
					const regex = new RegExp(this.data.animations);
					rootInstance.animations = gltf.animations.filter( (clip) => regex.test(clip.name) );
				}

				if(this.data.tracks)
				{
					const regex = new RegExp(this.data.tracks);
					rootInstance.animations = gltf.animations.map( (clip) =>
					{
						const tracks = clip.tracks.filter( trk => regex.test(trk.name) );
						if(tracks.length == clip.tracks.length)
							return clip;

						const clone = clip.clone();
						clone.tracks = tracks;
						return clone;
					});
				}
				
				this.el.setObject3D(object3Dkey, rootInstance);
			},
			(reason) => console.dir(reason)
		);
	}
} );
